import { Facebook, Mail, ShoppingBag, Phone, Twitter, Monitor, HelpCircle, Building2, Lock, Bot } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import type { Mail as ConversacionEnLista } from "@/lib/data"
import { getStatusDisplayName, getStatusStyles } from "@/lib/conversation-types"
import { useTranslations } from "@/hooks/use-translations"
import { ConversationStatusSelect } from "./conversation-status-select"
import { COLOR_DE_CANAL, DEGRADADO_DE_INSTAGRAM, estiloDeCanal } from '@/lib/colores-de-canal'

type ConversationStatus = 'active' | 'needs_review' | 'archived' | 'completed' | 'ai_responding'

interface ConversationCardProps {
  id: string
  title: string
  description: string
  // La misma union que `Mail['platform']`: esta lista se habia quedado sin
  // "avatar" y el desajuste solo se veia al tocar el fichero.
  platform: ConversacionEnLista['platform']
  time: string // This will use displayDate
  /** Las lineas de WhatsApp de este cliente. Vacio si solo tiene una. */
  lines?: { id: string; label: string; unread: number; isInternal: boolean }[]
  clientInfo: {
    name: string
    phone?: string
    email?: string
  }
  status: ConversationStatus
  isAnalyzing?: boolean
  onSelect: (id: string) => void
  isSelected?: boolean
  onStatusChange?: (conversationId: string, newStatus: 'active' | 'needs_review' | 'archived' | 'completed') => void
  unreadCount?: number
}

const platformIcons = {
  whatsapp: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={COLOR_DE_CANAL.whatsapp}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M3 21l1.9-5.7a8.5 8.5 0 113.8 3.8z" />
    </svg>
  ),
  instagram: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="url(#instagram-gradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <defs>
        <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={DEGRADADO_DE_INSTAGRAM[0]} />
          <stop offset="50%" stopColor={DEGRADADO_DE_INSTAGRAM[1]} />
          <stop offset="100%" stopColor={DEGRADADO_DE_INSTAGRAM[2]} />
        </linearGradient>
      </defs>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  ),
  facebook: <Facebook className="h-4 w-4" style={estiloDeCanal('facebook')} />,
  email: <Mail className="h-4 w-4" style={estiloDeCanal('email')} />,
  web: <Monitor className="h-4 w-4 text-primary" />,
  shopify: <ShoppingBag className="h-4 w-4" style={estiloDeCanal('shopify')} />,
  phone_call: <Phone className="h-4 w-4" style={estiloDeCanal('phone_call')} />,
  betterplace: <Building2 className="h-4 w-4" style={estiloDeCanal('betterplace')} />,
  wordpress: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={COLOR_DE_CANAL.wordpress}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M6.5 12l3 7.5M12 4.5l2 8-3.5 7M17.5 12l-3.5 8" />
    </svg>
  ),
  twitter: <Twitter className="h-4 w-4" style={estiloDeCanal('twitter')} />,
  // Faltaba: `Mail['platform']` incluye "avatar" desde que existe ese canal,
  // pero este mapa no, y sin el la tarjeta de una conversacion de avatar no
  // pintaba icono.
  avatar: <Bot className="h-4 w-4 text-primary" />,
  other: <HelpCircle className="h-4 w-4 text-muted-foreground" />,
}

export function ConversationCard({ 
  id,
  title,
  description,
  platform,
  lines = [],
  time,
  clientInfo,
  status,
  isAnalyzing = false,
  onSelect,
  isSelected = false,
  onStatusChange,
  unreadCount = 0,
}: ConversationCardProps) {
  const { t } = useTranslations()

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-lg border p-6 transition-all duration-300 ease-in-out cursor-pointer relative",
        isSelected
          // Elegida: el degradado, el anillo y la barra de la izquierda. Habia
          // ademas una sombra interior de color -una cuarta forma de decir lo
          // mismo, y la unica que no cambia con el tema-.
          ? "bg-gradient-to-r from-primary/5 via-primary/5 to-transparent border-primary/20 ring-1 ring-primary/40"
          : "hover:bg-accent/50 border-border hover:shadow-md",
        "before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:rounded-full before:transition-all before:duration-300",
        isSelected && "before:bg-primary",
      )}
      onClick={() => onSelect(id)}
    >
      {unreadCount > 0 && (
        <div className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-xs font-bold text-destructive-foreground shadow-md border-2 border-background animate-in zoom-in-50 z-10">
          {unreadCount > 9 ? '9+' : unreadCount}
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          {(() => {
            const showAnalyzingTitle = isAnalyzing && (!title || title.trim() === "")
            const showAnalyzingDescription = isAnalyzing && (!description || description.trim() === "")
            return (
              <>
                <h3 className={cn("text-sm font-semibold leading-none", showAnalyzingTitle && "animate-pulse")}> 
                  {showAnalyzingTitle ? t('conversations.analyzing_title') : title}
            </h3>
                <p className="line-clamp-2 text-sm text-muted-foreground">
                  {showAnalyzingDescription ? t('conversations.analyzing_description') : description}
                </p>
              </>
            )
          })()}
            </div>
        <div className="flex flex-col items-end gap-2">
            {/* Status select or badge */}
            {onStatusChange && platform !== 'phone_call' ? (
              <ConversationStatusSelect
                conversationId={id}
                currentStatus={status}
                onStatusChange={onStatusChange}
                disabled={false} // Siempre habilitado para permitir cambios de estado
              />
            ) : (
              <Badge variant="outline" className={cn("h-8 px-3", getStatusStyles(status))}>
                {getStatusDisplayName(status, t)}
              </Badge>
            )}
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            {platformIcons[platform]}
            <span>{time}</span>
          </div>
        </div>
      </div>
      {/* min-w-0 + flex-wrap: un email no tiene espacios, asi que su min-content
          es la cadena entera y sin esto ensancha la tarjeta fuera del viewport,
          dejando el selector de estado inalcanzable en movil. */}
      <div className="mt-2 flex min-w-0 flex-wrap items-center text-xs text-muted-foreground">
        <span className="min-w-0 truncate">{t('conversations.client')}: {clientInfo.name}</span>
        {lines.length > 1 && (
          <span className="flex shrink-0 flex-wrap items-center gap-1">
            {lines.map((linea) => (
              <Badge
                key={linea.id}
                variant="outline"
                className="h-4 gap-1 px-1 text-[10px] font-normal"
              >
                {linea.isInternal && <Lock className="h-2.5 w-2.5" />}
                {linea.label}
                {linea.unread > 0 && <span className="text-primary">·{linea.unread}</span>}
              </Badge>
            ))}
          </span>
        )}
        {clientInfo.phone && (
          <>
            <span className="mx-1">•</span>
            <span className="shrink-0">{clientInfo.phone}</span>
          </>
        )}
        {clientInfo.email && (
          <>
            <span className="mx-1">•</span>
            <span className="min-w-0 truncate" title={clientInfo.email}>{clientInfo.email}</span>
          </>
        )}
      </div>
    </div>
  )
}